/**
 * Spec Phase 5 Slice 5E — ParagraphBlock with contentEditable
 * + debounced auto-save.
 *
 * The body becomes a contentEditable surface when:
 *   - the parent section is NOT auto-generated (auto sections
 *     compute their content live in Phase 7)
 *   - the parent section is NOT frozen (freeze locks edits)
 *
 * Numeral chip preservation:
 *   Each <span class="s6-num" data-num="N">N</span> is rendered
 *   with contentEditable={false} so the browser treats it as an
 *   inert single block. Backspace at a chip boundary deletes the
 *   whole chip (rather than corrupting the digit text).
 *
 * Debounced auto-save:
 *   onInput fires on every keystroke; the store's
 *   updateParagraphLocalAndQueueSave applies the optimistic local
 *   update + schedules a PATCH 600ms after the last keystroke.
 *   The store also takes care of error surfacing + lastSavedAt.
 *
 * Cursor stability:
 *   We set innerHTML imperatively on mount + when the paragraph_id
 *   changes — never on every render. React's diff doesn't reach
 *   inside dangerouslySetInnerHTML, so the cursor stays put as the
 *   drafter types. The store mirror IS updated on every input so
 *   any consumer (e.g. NumeralChip in the rail) sees fresh content.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  AlertTriangle,
  Link,
  Loader2,
  Lock,
  Plus,
  Sparkles,
  Trash2,
  Unlock,
} from 'lucide-react';
import { useSpecStore } from '@/store/specStore';
import type {
  SpecParagraph,
  SpecSectionRow,
  SpecSectionVerifyIssue,
} from '@/api/client';
import { verifyIssueKindLabel } from './verifyIssueLabels';
import { publish } from '@/lib/numeralEventBus';
import { FEATURE_DRAG_MIME } from './FeatureMapDock';
import { GLOSSARY_DRAG_MIME } from './GlossaryDock';
import ParagraphAiPopover from './ParagraphAiPopover';
import ParagraphDeleteConfirm from './ParagraphDeleteConfirm';

interface Props {
  paragraph: SpecParagraph;
  /** When false (auto-generated section, frozen section), the
   * body renders read-only. */
  editable?: boolean;
  /** Phase 8 Slice 8C — transient issues from a section verify
   * scoped to this paragraph. Rendered as inline badges above
   * the body when non-empty. */
  verifyIssues?: SpecSectionVerifyIssue[];
  /** BUG-055 — patent-title rendering. When set to 'title' the
   * block hides the [NNNN] paragraph-number prefix and applies
   * the centred / uppercase / weight-600 styling defined by the
   * .s6-title-line CSS class so the Title section reads as a
   * patent title rather than a numbered paragraph. The body
   * stays contenteditable when `editable` is true. */
  variant?: 'title';
}

const railIcon =
  'inline-flex h-6 w-6 items-center justify-center rounded-sm text-zinc-300 ' +
  'opacity-0 group-hover:opacity-100 hover:text-zinc-700 hover:bg-zinc-100 ' +
  'disabled:cursor-not-allowed transition-opacity';

/** Lock every numeral chip with contentEditable=false so the
 * browser treats it as an inert block. Inputs an HTML string and
 * returns the same string with the contenteditable attribute set
 * on every s6-num span (idempotent — no-op when already locked).
 */
function _lockNumeralChips(html: string): string {
  return html.replace(
    /<span\b([^>]*?\bclass\s*=\s*"s6-num"[^>]*?)>/gi,
    (match, attrs: string) => {
      if (/contenteditable=/i.test(attrs)) return match;
      return `<span${attrs} contenteditable="false">`;
    },
  );
}

export default function ParagraphBlock({
  paragraph,
  editable = true,
  verifyIssues = [],
  variant,
}: Props): JSX.Element {
  const activeParagraphId = useSpecStore((s) => s.activeParagraphId);
  const setActiveParagraph = useSpecStore((s) => s.setActiveParagraph);
  const updateParagraphLocalAndQueueSave = useSpecStore(
    (s) => s.updateParagraphLocalAndQueueSave,
  );
  const matterId = useSpecStore((s) => s.matterId);
  const referenceSigns = useSpecStore((s) => s.referenceSigns);
  const addParagraphToSection = useSpecStore((s) => s.addParagraphToSection);
  const deleteParagraph = useSpecStore((s) => s.deleteParagraph);
  const toggleParagraphLock = useSpecStore((s) => s.toggleParagraphLock);
  const expandParagraph = useSpecStore((s) => s.expandParagraph);
  const anchorFeature = useSpecStore((s) => s.anchorFeature);
  const lastAnchoredParagraphId = useSpecStore(
    (s) => s.lastAnchoredParagraphId,
  );
  const showToast = useSpecStore((s) => s.showToast);
  const paragraphMutationStatus = useSpecStore(
    (s) => s.paragraphMutationStatus,
  );
  const paragraphMutationTargetId = useSpecStore(
    (s) => s.paragraphMutationTargetId,
  );
  // BUG-015 — server-side content-replace signal. Bumped by
  // expandParagraph / tightenParagraph / applyNlEdit / AI-suggest
  // popover paths. Drives the imperative innerHTML reset effect
  // below so server replacements reach the DOM without
  // disturbing the cursor during local typing.
  const serverVersion = useSpecStore(
    (s) => s.paragraphServerVersion[paragraph.id] ?? 0,
  );
  const isActive = paragraph.id === activeParagraphId;
  const isThisExpanding =
    paragraphMutationStatus === 'expanding' &&
    paragraphMutationTargetId === paragraph.id;
  const isThisTightening =
    paragraphMutationStatus === 'tightening' &&
    paragraphMutationTargetId === paragraph.id;
  const isMutatingThis = isThisExpanding || isThisTightening;

  const [aiPopoverOpen, setAiPopoverOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deletePending, setDeletePending] = useState(false);

  const bodyRef = useRef<HTMLDivElement | null>(null);

  // Imperative innerHTML set on mount + paragraph_id change + on
  // every server-side content replacement (BUG-015). We
  // intentionally DO NOT depend on paragraph.body_html — re-setting
  // it on every keystroke (since body_html updates in-store via
  // updateParagraphLocalAndQueueSave) would jump the cursor. The
  // serverVersion counter is bumped only by LLM-driven mutations
  // (expand / tighten / NL-edit / AI suggest), so it's the right
  // signal: server-replace yes, local typing no.
  useEffect(() => {
    if (!bodyRef.current) return;
    const initial = _lockNumeralChips(paragraph.body_html || '');
    if (bodyRef.current.innerHTML !== initial) {
      bodyRef.current.innerHTML = initial;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paragraph.id, serverVersion]);

  // Phase 7 Slice 7E — chip event delegation + warn-state mark.
  // The body is innerHTML-managed (cursor-stable) so chips can't
  // be React components. We attach delegated listeners + decorate
  // amber-warn for any chip whose data-num isn't in the registry.
  useEffect(() => {
    const root = bodyRef.current;
    if (!root) return;
    const known = new Set(referenceSigns.map((r) => r.numeral));

    // Annotate / unannotate warn-state on each chip.
    const chips = root.querySelectorAll<HTMLElement>('span.s6-num');
    chips.forEach((chip) => {
      const num = chip.getAttribute('data-num') ?? chip.textContent ?? '';
      const isUndefined = num !== '' && !known.has(num);
      chip.classList.toggle('s6-num-warn', isUndefined);
      if (isUndefined) chip.dataset.undefined = 'true';
      else delete chip.dataset.undefined;
    });

    const closestChip = (target: EventTarget | null): HTMLElement | null => {
      if (!(target instanceof Element)) return null;
      return target.closest('span.s6-num');
    };

    const onMouseEnter = (e: Event): void => {
      const chip = closestChip(e.target);
      if (!chip) return;
      const numeral = chip.getAttribute('data-num') ?? chip.textContent ?? '';
      publish({
        kind: 'numeral:hover',
        numeral,
        undefined_: chip.dataset.undefined === 'true',
        source: 'paragraph',
      });
    };
    const onMouseLeave = (e: Event): void => {
      const chip = closestChip(e.target);
      if (!chip) return;
      const numeral = chip.getAttribute('data-num') ?? chip.textContent ?? '';
      publish({
        kind: 'numeral:hover-out',
        numeral,
        undefined_: chip.dataset.undefined === 'true',
        source: 'paragraph',
      });
    };
    const onClick = (e: Event): void => {
      const chip = closestChip(e.target);
      if (!chip) return;
      e.stopPropagation();
      const numeral = chip.getAttribute('data-num') ?? chip.textContent ?? '';
      publish({
        kind: 'numeral:click',
        numeral,
        undefined_: chip.dataset.undefined === 'true',
        source: 'paragraph',
      });
    };

    // mouseenter/leave don't bubble; use mouseover/mouseout for
    // delegation, plus an entry-guard so we publish once per chip.
    const onMouseOver = (e: MouseEvent): void => {
      const chip = closestChip(e.target);
      const related = closestChip(e.relatedTarget);
      if (chip && chip !== related) onMouseEnter(e);
    };
    const onMouseOut = (e: MouseEvent): void => {
      const chip = closestChip(e.target);
      const related = closestChip(e.relatedTarget);
      if (chip && chip !== related) onMouseLeave(e);
    };

    root.addEventListener('mouseover', onMouseOver);
    root.addEventListener('mouseout', onMouseOut);
    root.addEventListener('click', onClick);
    return () => {
      root.removeEventListener('mouseover', onMouseOver);
      root.removeEventListener('mouseout', onMouseOut);
      root.removeEventListener('click', onClick);
    };
  }, [paragraph.id, paragraph.body_html, referenceSigns]);

  const handleInput = useCallback(() => {
    if (!bodyRef.current || !editable) return;
    const html = bodyRef.current.innerHTML;
    const text = (bodyRef.current.textContent || '').replace(/\s+/g, ' ').trim();
    updateParagraphLocalAndQueueSave({
      paragraphId: paragraph.id,
      sectionId: paragraph.spec_section_id,
      bodyHtml: html,
      bodyText: text,
    });
  }, [editable, paragraph.id, paragraph.spec_section_id, updateParagraphLocalAndQueueSave]);

  const hasIssues = verifyIssues.length > 0;
  const [dragOver, setDragOver] = useState(false);
  const justAnchored = lastAnchoredParagraphId === paragraph.id;
  const containerCls = [
    's6-para group relative',
    // BUG-055 — title variant collapses the 56px [NNNN] column so
    // the body fills the full row; without this the centred title
    // text floats 56px right of true centre.
    variant === 'title' ? 's6-para-title' : '',
    isActive ? 'rounded-sm bg-blue-50/40' : '',
    hasIssues ? 's6-para-warn' : '',
    dragOver ? 's6-para-drop-target' : '',
    justAnchored ? 's6-para-feature-drop' : '',
    // Paragraph-level freeze cue: amber left border so the
    // drafter sees at a glance which paragraphs survive
    // regeneration. Matches the "preserve" semantic colour used
    // in the SectionDiffPanel for locked rows.
    paragraph.is_locked ? 'border-l-2 border-amber-400 pl-2' : '',
  ].filter(Boolean).join(' ');

  const _hasSpecDragType = useCallback(
    (e: React.DragEvent<HTMLDivElement>): boolean => {
      const types = Array.from(e.dataTransfer.types);
      return types.includes(FEATURE_DRAG_MIME)
        || types.includes(GLOSSARY_DRAG_MIME);
    },
    [],
  );

  const handleDragOver = useCallback(
    (e: React.DragEvent<HTMLDivElement>): void => {
      // Only intercept spec-side drags (feature mapping + glossary
      // term). Other drag types pass through.
      if (!_hasSpecDragType(e)) return;
      e.preventDefault();
      const types = Array.from(e.dataTransfer.types);
      e.dataTransfer.dropEffect = types.includes(FEATURE_DRAG_MIME)
        ? 'link' : 'copy';
      if (!dragOver) setDragOver(true);
    },
    [dragOver, _hasSpecDragType],
  );

  const handleDragLeave = useCallback(
    (e: React.DragEvent<HTMLDivElement>): void => {
      if (!_hasSpecDragType(e)) return;
      setDragOver(false);
    },
    [_hasSpecDragType],
  );

  const handleDrop = useCallback(
    async (e: React.DragEvent<HTMLDivElement>): Promise<void> => {
      // Glossary path takes precedence — it drops a literal token
      // at the cursor without server roundtrip; feature drag is
      // server-bound. Only one MIME present per drag in practice.
      const glossaryTerm = e.dataTransfer.getData(GLOSSARY_DRAG_MIME);
      if (glossaryTerm) {
        e.preventDefault();
        setDragOver(false);
        if (!editable) {
          showToast('Cannot insert glossary term into a frozen section.');
          return;
        }
        // Insert at the caret position when the drop happened
        // INSIDE a contentEditable body with an active selection;
        // otherwise append at the end of the body.
        const root = bodyRef.current;
        if (!root) return;
        const sel = window.getSelection();
        let range: Range | null = null;
        if (sel && sel.rangeCount > 0) {
          // Use caretRangeFromPoint when available — gives us the
          // drop-coordinate caret position rather than the prior
          // selection.
          const docAny = document as Document & {
            caretRangeFromPoint?: (x: number, y: number) => Range | null;
            caretPositionFromPoint?: (x: number, y: number) => {
              offsetNode: Node; offset: number;
            } | null;
          };
          if (docAny.caretRangeFromPoint) {
            range = docAny.caretRangeFromPoint(e.clientX, e.clientY);
          } else if (docAny.caretPositionFromPoint) {
            const cp = docAny.caretPositionFromPoint(e.clientX, e.clientY);
            if (cp) {
              range = document.createRange();
              range.setStart(cp.offsetNode, cp.offset);
              range.collapse(true);
            }
          }
        }
        if (!range || !root.contains(range.startContainer)) {
          // Fallback: append at the end.
          root.appendChild(document.createTextNode(' ' + glossaryTerm + ' '));
        } else {
          range.deleteContents();
          range.insertNode(document.createTextNode(glossaryTerm));
        }
        // Sync the change back to the store + queue auto-save.
        const html = root.innerHTML;
        const text = (root.textContent || '').replace(/\s+/g, ' ').trim();
        updateParagraphLocalAndQueueSave({
          paragraphId: paragraph.id,
          sectionId: paragraph.spec_section_id,
          bodyHtml: html,
          bodyText: text,
        });
        showToast(
          `Glossary term “${glossaryTerm}” inserted into paragraph `
          + `[${paragraph.paragraph_number}]`,
        );
        return;
      }

      const featureId = e.dataTransfer.getData(FEATURE_DRAG_MIME);
      if (!featureId) return;
      e.preventDefault();
      setDragOver(false);
      const r = await anchorFeature(featureId, paragraph.id);
      if (r.kind === 'ok') {
        showToast(
          `Feature ${r.featureTag ?? ''} anchored to paragraph `
          + `[${paragraph.paragraph_number}]`,
        );
      } else {
        showToast(`Anchor failed: ${r.message ?? ''}`);
      }
    },
    [
      anchorFeature, editable, paragraph.id, paragraph.paragraph_number,
      paragraph.spec_section_id, showToast, updateParagraphLocalAndQueueSave,
    ],
  );

  return (
    <div
      className={containerCls}
      data-testid={`spec-paragraph-block-${paragraph.paragraph_number}`}
      onClick={() => setActiveParagraph(paragraph.id)}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      role="article"
    >
      {variant !== 'title' && (
        <span className="s6-para-num">[{paragraph.paragraph_number}]</span>
      )}
      {hasIssues && (
        <div
          className="s6-para-warn-tag mt-1 flex flex-col gap-0.5 rounded-sm bg-amber-50 px-2 py-1 text-[11px] text-amber-900"
          data-testid={`spec-paragraph-verify-${paragraph.paragraph_number}`}
        >
          {verifyIssues.map((it, i) => (
            <div key={i} className="flex items-start gap-1">
              <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
              <span>
                <strong className="text-[10px]">{verifyIssueKindLabel(it.kind)}</strong>
                {' · '}
                {it.detail}
                {it.suggestion ? <> · <em>{it.suggestion}</em></> : null}
              </span>
            </div>
          ))}
        </div>
      )}
      <div
        ref={bodyRef}
        className={
          variant === 'title'
            ? 's6-para-body s6-title-line focus:outline-none'
            : 's6-para-body focus:outline-none'
        }
        contentEditable={editable}
        suppressContentEditableWarning
        onInput={handleInput}
        spellCheck={editable}
        data-testid={`spec-paragraph-body-${paragraph.paragraph_number}`}
      />
      {/* Hover rail — Slice 6E LIVE.
          Add (append paragraph) / Anchor (copy permalink) /
          AI (expand with intent popover) / Delete (confirm). */}
      <div className="absolute right-0 top-0 flex flex-col gap-1 pr-1 pt-1">
        <button
          type="button"
          className={railIcon}
          disabled={!editable || isMutatingThis}
          onClick={async (e) => {
            e.stopPropagation();
            // Phase 12F — hover-rail Add inserts AFTER this
            // paragraph instead of at the section end. Drafters
            // expect the new paragraph to appear "right here" when
            // they hover and click Add on a specific paragraph.
            const newPara = await addParagraphToSection(
              paragraph.spec_section_id,
              { afterParagraphId: paragraph.id },
            );
            if (newPara) {
              showToast(`Added paragraph [${newPara.paragraph_number}]`);
            }
          }}
          aria-label="Add paragraph after this one"
          title="Add paragraph after this one"
          data-testid="spec-paragraph-add"
        >
          <Plus className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          className={railIcon}
          onClick={async (e) => {
            e.stopPropagation();
            // Anchor / permalink: build a stable URL keyed by the
            // matter + paragraph_number (gap-stable per EP). Phase 9
            // surfaces these in revision proposals.
            const url =
              typeof window !== 'undefined' && matterId
                ? `${window.location.origin}/matter/${matterId}/spec#para-${paragraph.paragraph_number}`
                : `#para-${paragraph.paragraph_number}`;
            try {
              if (navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(url);
                showToast('Permalink copied');
              } else {
                showToast(`Permalink: ${url}`);
              }
            } catch {
              showToast(`Permalink: ${url}`);
            }
          }}
          aria-label="Copy paragraph permalink"
          title="Copy permalink to this paragraph"
          data-testid="spec-paragraph-anchor"
        >
          <Link className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          className={railIcon}
          disabled={!editable || isMutatingThis}
          onClick={(e) => {
            e.stopPropagation();
            setAiPopoverOpen(true);
          }}
          aria-label="AI suggest"
          title="Open AI suggest popover"
          data-testid="spec-paragraph-ai"
        >
          {isThisExpanding ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Sparkles className="h-3.5 w-3.5" />
          )}
        </button>
        <button
          type="button"
          // Locked paragraphs keep the icon visible at full opacity
          // (drafter needs an at-a-glance cue that the row is
          // preserve-on-regenerate). Unlocked paragraphs use the
          // standard fade-on-hover rail.
          className={
            paragraph.is_locked
              ? railIcon.replace(
                  'opacity-0 group-hover:opacity-100 ',
                  '',
                ) + ' text-amber-600 hover:text-amber-700'
              : railIcon
          }
          onClick={async (e) => {
            e.stopPropagation();
            const outcome = await toggleParagraphLock(
              paragraph.spec_section_id, paragraph.id,
            );
            if (outcome.kind === 'ok') {
              showToast(
                outcome.isLocked
                  ? `Locked paragraph [${paragraph.paragraph_number}] — preserved through regenerate`
                  : `Unlocked paragraph [${paragraph.paragraph_number}]`,
              );
            } else if (outcome.kind === 'error') {
              showToast(`Lock toggle failed: ${outcome.message}`);
            }
          }}
          aria-label={paragraph.is_locked ? 'Unlock paragraph' : 'Lock paragraph'}
          title={paragraph.is_locked
            ? 'Unlock paragraph (will be regenerated on next section regenerate)'
            : 'Lock paragraph (preserved through section regenerate)'}
          data-testid="spec-paragraph-lock"
        >
          {paragraph.is_locked
            ? <Lock className="h-3.5 w-3.5" />
            : <Unlock className="h-3.5 w-3.5" />}
        </button>
        <button
          type="button"
          className={railIcon}
          disabled={!editable || isMutatingThis || deletePending}
          onClick={(e) => {
            e.stopPropagation();
            setDeleteConfirmOpen(true);
          }}
          aria-label="Delete paragraph"
          title="Delete this paragraph"
          data-testid="spec-paragraph-delete"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
      {aiPopoverOpen && (
        <ParagraphAiPopover
          paragraphNumber={paragraph.paragraph_number}
          isPending={isThisExpanding}
          onCancel={() => setAiPopoverOpen(false)}
          onSubmit={async (drafterIntent) => {
            const outcome = await expandParagraph({
              paragraphId: paragraph.id,
              drafterIntent,
            });
            if (outcome.kind === 'ok') {
              setAiPopoverOpen(false);
              showToast(`AI applied to paragraph [${paragraph.paragraph_number}]`);
            } else if (outcome.kind === 'error') {
              showToast(`AI failed: ${outcome.message}`);
            } else if (outcome.kind === 'locked') {
              showToast('Specification locked: drawings are no longer frozen.');
              setAiPopoverOpen(false);
            }
          }}
        />
      )}
      {deleteConfirmOpen && (
        <ParagraphDeleteConfirm
          paragraphNumber={paragraph.paragraph_number}
          isPending={deletePending}
          onCancel={() => setDeleteConfirmOpen(false)}
          onConfirm={async () => {
            setDeletePending(true);
            const outcome = await deleteParagraph(paragraph.id);
            setDeletePending(false);
            if (outcome.kind === 'ok') {
              setDeleteConfirmOpen(false);
              showToast(
                `Paragraph [${paragraph.paragraph_number}] deleted`,
              );
            }
          }}
        />
      )}
    </div>
  );
}

/** Helper consumed by PaperSurface: a section is "editable" when
 * neither auto-generated nor frozen. */
export function isSectionEditable(section: SpecSectionRow | null): boolean {
  if (!section) return false;
  return !section.is_auto_generated && !section.is_frozen;
}
